import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type { AnalyticsData, FundSettings, PipelineData, RosterMember, StaffingData } from '../types';
import { setStageOrder } from '../utils/stageOrder';

export interface StageMapping {
  stage_value: string;
  section: string;
  order: number;
}

// Fund II Overview settings (committed/invested/called/reserved-exp + the
// per-deal concentration cap). User-auth read; the Overview bar renders from it.
export function useFundSettings() {
  return useQuery<FundSettings>({
    queryKey: ['fund-settings'],
    queryFn: () => apiFetch<FundSettings>('/api/fund-settings'),
    staleTime: 30 * 60 * 1000,
  });
}

export function useStages() {
  return useQuery<StageMapping[]>({
    queryKey: ['stages'],
    queryFn: async () => {
      const stages = await apiFetch<StageMapping[]>('/api/stages');
      setStageOrder(stages.map(s => s.stage_value));
      return stages;
    },
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * The Affinity-fed v1 snapshot. `enabled` exists so a surface that does not read it does not
 * fetch it — see the call in `App.tsx`, which gates on `SNAPSHOT_TABS`.
 *
 * **The 10s `refetchInterval` was deleted 2026-08-17, and must not come back in that form.** It
 * re-fetched whenever ANY deal reported `activity_stale`, so that freshly fetched activity would
 * land after `/pipeline/activity-refresh` finished. Two things made it pathological:
 *
 *  - `activity_stale` is `activity_fetched_at < now - activity_cache_ttl_hours` (2 h,
 *    `pipeline_builder.py`), and the Affinity refresh schedules were switched off at the cut.
 *    Prod's whole latest snapshot is stamped `2026-08-11 22:25:06`, so the predicate is now
 *    permanently true and can never clear itself.
 *  - nothing in the UI calls `/pipeline/activity-refresh` any more — the two hooks that did were
 *    deleted in the 2026-08-11 v1/v2 swap (see the note in `App.tsx`). The poll was waiting on a
 *    job the browser can no longer start.
 *
 * Combined with the ungated call in `App.tsx`, that meant every open tab re-fetched the heaviest
 * endpoint in the app every 10 seconds, on every page, forever — against a single gunicorn worker.
 * If a refresh-then-settle behaviour is ever needed again, drive it off the mutation that started
 * the refresh, not off a data predicate that no longer has anything to clear it.
 */
export function usePipeline(enabled = true) {
  return useQuery<PipelineData>({
    queryKey: ['pipeline'],
    queryFn: () => apiFetch<PipelineData>('/api/pipeline'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

// ⛔ THE REFRESH HOOKS AND THEIR ENDPOINTS ARE ALL GONE NOW (post-Affinity S0, 2026-09-01).
//
// `useFullRefreshPipeline` went on 2026-08-10 with the header's Full Refresh button;
// `useBaselineRefresh` + `useActivityRefresh` on 2026-08-11 with the once-per-session refresh
// App.tsx fired on every load; `useRefreshPipeline` (POST /api/pipeline/quick-refresh) had no caller
// at all after that and goes here. The four backend routes went with it — each was a live Affinity
// snapshot into `opportunities`, a table frozen since the 2026-08-08 cut, and the subscription
// terminates 2026-09-30. There is nothing to re-add a hook for.

export function useTestNotify() {
  return useMutation<{ emails_sent: number; deals_flagged: number; errors: string[] }>({
    mutationFn: () =>
      apiFetch('/api/pipeline/notify?recipients=investment&test_mode=true', { method: 'POST' }),
  });
}

// `useDealActivity` lived here — a `useQuery` over `GET /api/deals/{id}/activity` shipped with
// `enabled: false` and ZERO component callers, so it never fired. Deleted with the endpoint by the
// Pillar-D write funeral (crm-of-record-plan §5, 2026-08-27): that endpoint was the last live-Affinity
// read leg on the Deals tab, and the native replacement is the evidence/timeline path.

// ⛔ `useSectors` and the sector write hooks (`useCreateSector`, `useUpdateSector`,
// `useAddSectorStatus`, `useUpdateSectorAssignments`, `useDeleteSector`) — and `useSectorHistory`,
// which had had no caller for a while — were deleted on
// 2026-09-01 with their only callers: the v1 board's `SectorEditModal` and the Agenda slot editor.
// `/api/sectors*` stays — Sector Coverage, Intel and the pipeline builder all read the editorial
// sectors table; what is gone is the browser's write path into it.

export function useRoster() {
  return useQuery<RosterMember[]>({
    queryKey: ['roster'],
    queryFn: () => apiFetch<RosterMember[]>('/api/roster'),
    staleTime: 30 * 60 * 1000,
  });
}

export interface RosterAudit {
  missing_persons: { person_id: number; email: string; name: string; last_seen_at: string | null }[];
  incomplete_roster: { email: string; name: string; missing_fields: string[] }[];
  total_issues: number;
}

export function useRosterAudit(enabled: boolean) {
  return useQuery<RosterAudit>({
    queryKey: ['roster-audit'],
    queryFn: () => apiFetch<RosterAudit>('/api/roster/audit'),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export interface PersonHistoryCell {
  week_start: string;
  assigned: boolean;
  role: string | null;
  meetings: number;
  emails: number;
}

export interface PersonHistoryDeal {
  // DASH-3 3-3 — the matrix spans both eras, so a deal carries whichever id its era has: the Affinity
  // opportunity id from the frozen builder, `crm_list_entry.id` from the native one. Never both.
  affinity_opportunity_id: number | null;
  entry_id?: number | null;
  name: string;
  stage: string;
  category: 'portfolio' | 'new_deal' | 'other';
  weeks_active: number;
  total_activity: number;
  cells: PersonHistoryCell[];
}

export interface PersonHistory {
  person_email: string;
  person_name: string | null;
  weeks: string[];
  deals: PersonHistoryDeal[];
  /** 'native' once the payload comes from the native read model; absent from the frozen builder. */
  source?: 'native';
  /** The day the sources changed hands — metadata, not a seam marker (DASH-3 §7 ruling 2). */
  source_change_date?: string | null;
  /** The IOT's list id — native payloads only; the record-panel link needs it. */
  list_id?: number | null;
}

export function usePersonHistory(email: string | null, weeks: number) {
  return useQuery<PersonHistory>({
    queryKey: ['person-history', email, weeks],
    queryFn: () =>
      apiFetch<PersonHistory>(`/api/roster/${encodeURIComponent(email || '')}/history?weeks=${weeks}`),
    enabled: !!email,
    staleTime: 2 * 60 * 1000,
  });
}

export function useStaffing(activityDays: number = 30) {
  return useQuery<StaffingData>({
    queryKey: ['staffing', activityDays],
    queryFn: () => apiFetch<StaffingData>(`/api/staffing?activity_days=${activityDays}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnalytics() {
  return useQuery<AnalyticsData>({
    queryKey: ['analytics'],
    queryFn: () => apiFetch<AnalyticsData>('/api/analytics/summary'),
    staleTime: 5 * 60 * 1000,
  });
}

// ⛔ `useTeamActivity` (GET /api/analytics/team-activity) went with the Metrics tab on
// 2026-09-01 (post-Affinity S1). The endpoint and `team_activity_builder` went with it: Overview
// dropped its summary cards in S2, Metrics was the only other reader, and the plan is explicit
// that the builder must not be kept alive to serve one card row.

// ⛔ The v1 board's dismiss/restore mutations (`useDismissDeal`, `useRestoreDeal`,
// `useDismissAllRecentlyDecided`, `useDismissWonDeal`, `useRestoreWonDeal`,
// `useDismissAllRecentlyClosed`) were deleted on 2026-09-01 with the Recently Passed/Lost and
// Recently Closed sections, their only callers. The ROUTES they called are deliberately still
// there: they write `stage_transitions.dismissed_at`, and `stage_transitions` stays a live read
// source (plan decision 36). Deal Updates has its own `useDismissDeal` in `useDeals.ts` — a
// different endpoint, unaffected.
